import React from 'react'
import { useTable } from '@refinedev/antd'
import { useList } from '@refinedev/core'
import { Pagination } from 'antd'
import { brand, initials } from '../../theme/brand'
import { ListPage, SectionCard, AvatarDisc, Pill, EmptyHint, type Stat, type Tone } from '../../theme/listkit'
import { Activity as ActivityIcon, UsersRound, HandHeart, CalendarCheck } from '../../components/icons'

interface Activity {
  id: string
  kind: string
  occurred_at: string
  actor: string
  summary: string
}

// One-row admin summary view for the stat deck (kind counts). Computed in
// Postgres so the deck no longer counts over the whole feed on the main thread.
interface ActivityFeedSummary {
  id: number
  total: number | null
  signups: number | null
  donations: number | null
  rsvps: number | null
}

/** Pill tone per activity kind. */
const KIND_TONE: Record<string, Tone> = {
  signup: 'jaguar',
  donation: 'gold',
  rsvp: 'green',
}

const fmtWhen = (v: string | null) =>
  v
    ? new Date(v).toLocaleString(undefined, {
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
      })
    : '—'

/**
 * Activity — read-only unified feed from the activity_feed view (admin-gated),
 * spanning sign-ups, donations and RSVPs, newest first. Rendered as a
 * ceremonial timeline rather than a plain table. The row source is a BOUNDED,
 * paginated useTable (was pagination mode:'off', which mounted EVERY row), so
 * only one page of activity is fetched and rendered at a time. The stat deck
 * reads its kind counts from a one-row admin summary view instead of counting
 * the whole feed client-side.
 */
export function ActivityFeedList() {
  const { tableProps, currentPage, pageSize, setCurrentPage } = useTable<Activity>({
    resource: 'activity_feed',
    pagination: { pageSize: 25 },
    sorters: { initial: [{ field: 'occurred_at', order: 'desc' }] },
  })
  const rows = [...((tableProps.dataSource ?? []) as readonly Activity[])]
  const total =
    tableProps.pagination && typeof tableProps.pagination.total === 'number'
      ? tableProps.pagination.total
      : 0

  // Stat deck: a single pre-aggregated row from the admin summary VIEW instead
  // of fetching the whole activity_feed and filtering by kind on the main thread.
  const { result: summaryRes } = useList<ActivityFeedSummary>({
    resource: 'admin_activity_feed_summary',
    pagination: { pageSize: 1 },
  })
  const summary = summaryRes?.data?.[0]

  const stats: Stat[] = [
    { label: 'Total', value: (summary?.total ?? 0).toLocaleString(), icon: ActivityIcon, tone: 'jaguar' },
    { label: 'Sign-ups', value: (summary?.signups ?? 0).toLocaleString(), icon: UsersRound, tone: 'jaguar' },
    { label: 'Donations', value: (summary?.donations ?? 0).toLocaleString(), icon: HandHeart, tone: 'gold' },
    { label: 'RSVPs', value: (summary?.rsvps ?? 0).toLocaleString(), icon: CalendarCheck, tone: 'green' },
  ]

  return (
    <ListPage
      intro={{
        eyebrow: 'Overview · Activity',
        description:
          'One unified stream of what is happening across the app — sign-ups, gifts and RSVPs, newest first. The pulse of the alumni community in real time.',
      }}
      stats={stats}
    >
      <SectionCard title="Activity Feed" sub="Across the app · newest first">
        {rows.length === 0 ? (
          <EmptyHint>No activity recorded yet.</EmptyHint>
        ) : (
          <div>
            {rows.map((a, i) => (
              <div
                key={a.id}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 12,
                  padding: '12px 0',
                  borderBottom: i < rows.length - 1 ? '1px solid ' + brand.cardLine : 'none',
                }}
              >
                <AvatarDisc initials={initials(a.actor)} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                    <span style={{ fontFamily: brand.fSans, fontWeight: 700, fontSize: 13, color: brand.ink }}>
                      {a.actor}
                    </span>
                    <Pill tone={KIND_TONE[a.kind] ?? 'neutral'}>{a.kind}</Pill>
                  </div>
                  <div style={{ fontFamily: brand.fSans, fontWeight: 500, fontSize: 13, color: brand.sub, marginTop: 3 }}>
                    {a.summary}
                  </div>
                </div>
                <span
                  style={{
                    fontFamily: brand.fSans,
                    fontWeight: 500,
                    fontSize: 11.5,
                    color: brand.sub,
                    flexShrink: 0,
                    whiteSpace: 'nowrap',
                  }}
                >
                  {fmtWhen(a.occurred_at)}
                </span>
              </div>
            ))}
            {total > pageSize ? (
              <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 14 }}>
                <Pagination
                  current={currentPage}
                  pageSize={pageSize}
                  total={total}
                  showSizeChanger={false}
                  onChange={(page) => setCurrentPage(page)}
                />
              </div>
            ) : null}
          </div>
        )}
      </SectionCard>
    </ListPage>
  )
}
